import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import useHistoryDismiss, { type HistoryDismissCloseOptions } from 'src/hooks/useHistoryDismiss';

type Props = {
  children?: React.ReactNode;
};

/**
 * Marker written into `window.history.state` when the mobile drawer pushes a
 * history entry, so a hardware/browser Back closes the drawer instead of
 * navigating away (ORI-464). The history mechanics now live in the shared
 * `useHistoryDismiss` hook (ORI-459).
 */
const SIDEBAR_HISTORY_KEY = 'lulaSidebar';

/**
 * Options for the mobile-drawer close handler. `viaNavigation` marks a close
 * that a router.push immediately follows (onLinkClick) so the handler skips its
 * history.back() and doesn't race the navigation (ORI-464).
 */
export type SidebarCloseOptions = HistoryDismissCloseOptions;

type SidebarToggleType = {
  sidebarCollapsedForDesktop: boolean;
  setSidebarCollapsedForDesktop: React.Dispatch<React.SetStateAction<boolean>>;
  expandedForMobile: boolean;
  setExpandedForMobile: React.Dispatch<React.SetStateAction<boolean>>;
  openSidebarOnMobile: () => void;
  handleCloseSidebarOnMobile: (options?: SidebarCloseOptions) => void;
  toggleSidebar: (e: React.MouseEvent<any, MouseEvent>) => void;
};

const sidebarToggleContext = createContext({} as SidebarToggleType);

const useSidebarSettings = () => {
  const [sidebarCollapsedForDesktop, setSidebarCollapsedForDesktop] = useState(false);
  const [expandedForMobile, setExpandedForMobile] = useState(false);

  const onHistoryDismiss = useCallback(() => setExpandedForMobile(false), []);

  // ORI-464 history participation via the shared hook (ORI-459).
  // `autoPush: false` keeps the marker push imperative inside
  // openSidebarOnMobile — a direct setExpandedForMobile(true) (e.g. another
  // flow's setter) must NOT create a history entry. `requireMarkerPopped:
  // false` preserves the drawer's original dialect: ANY popstate while open
  // closes it.
  const { markNavigating, pushEntry } = useHistoryDismiss(
    expandedForMobile,
    onHistoryDismiss,
    SIDEBAR_HISTORY_KEY,
    { autoPush: false, requireMarkerPopped: false },
  );

  // This func is used to open the sidebar in smaller screens.
  const openSidebarOnMobile = useCallback(() => {
    // Push a history entry once per open so hardware Back closes the drawer
    // instead of navigating (ORI-464). Guarded synchronously inside the hook,
    // so two same-tick opens can't double-push.
    pushEntry();
    setExpandedForMobile(true);
    // Functional update instead of reading `sidebarCollapsedForDesktop` from
    // the closure, so this handler no longer changes identity on every desktop
    // collapse. Returning `prev` when already expanded matches the previous
    // `if (collapsed)` guard (a no-op state write).
    setSidebarCollapsedForDesktop((prev) => (prev ? false : prev));
  }, [pushEntry]);

  // This func is used to close the sidebar in smaller screens. Always closes
  // synchronously (snappy dismiss); the history entry pushed on open is then
  // reconciled by the hook (ORI-464). A viaNavigation close (onLinkClick) is
  // flagged first so the hook skips its history.back() and doesn't race the
  // router.push. A real hardware Back never reaches this func; it's handled by
  // the hook's popstate listener.
  const handleCloseSidebarOnMobile = useCallback(
    (options?: SidebarCloseOptions) => {
      if (options?.viaNavigation) markNavigating();
      setExpandedForMobile(false);
    },
    [markNavigating],
  );

  const toggleSidebar = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      // Functional update so the toggle doesn't close over the current collapse
      // state — that closure was re-creating this handler on each collapse/expand,
      // and would re-create any future consumer memo keyed on its identity.
      setSidebarCollapsedForDesktop((prev) => !prev);
      handleCloseSidebarOnMobile();
    },
    [handleCloseSidebarOnMobile],
  );

  // Memoized so the provider value identity only changes when the state it
  // actually exposes changes. The three handlers are `[]`-stable in effect
  // (their only deps are the `[]`-stable `pushEntry` / `markNavigating` from
  // useHistoryDismiss), and the useState setters are stable by contract, so in
  // practice this object changes only on a real collapse/expand flip.
  return useMemo(
    () => ({
      sidebarCollapsedForDesktop,
      setSidebarCollapsedForDesktop,
      expandedForMobile,
      setExpandedForMobile,
      openSidebarOnMobile,
      handleCloseSidebarOnMobile,
      toggleSidebar,
    }),
    [
      sidebarCollapsedForDesktop,
      expandedForMobile,
      openSidebarOnMobile,
      handleCloseSidebarOnMobile,
      toggleSidebar,
    ],
  );
};

export const SidebarToggleProvider: React.FC<Props> = ({ children = '' }) => {
  const settings = useSidebarSettings();

  return <sidebarToggleContext.Provider value={settings}>{children}</sidebarToggleContext.Provider>;
};
export const useSidebarToggle = () => useContext(sidebarToggleContext);
